import React from 'react';
import { clsx } from 'clsx';
import { effectiveRates } from '@/services/trades/effective-rate';
import { ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import type { PositionDetail } from '@/types/trades.types';
import { GMRA_TRADE_TYPES } from '@/types/trades.types';
import type { AccrualEntry } from '@/types';
// Import primitives directly (not via the @/components/ui barrel) so the
// trade-detail tree doesn't pull in the inventory Table/cross-dnd chain the
// barrel re-exports (keeps unit tests that stub @/stores from breaking).
import { Figure } from '@/components/ui/Figure';
import { SignedValue } from '@/components/ui/SignedValue';
import { FIGURE_TIER_CLASS } from '@/components/ui/figure-styles';
import { formatDecimalString, toDecimalString } from '@/utils/decimal-display';
import {
  accrualCurrency,
  accruedByCurrency,
  deriveAccruedToDate,
  deriveMarginAdequacy,
  hasSettlementDailyReturn,
  isNullOrZeroDecimal,
  isTerminalStatus,
} from '../analytics/analytics-economics';
import { MARGIN_LABEL, verdictTextClass } from './margin-display';

interface KpiStripProps {
  trade: PositionDetail;
  accruals: AccrualEntry[] | undefined;
  isLoading: boolean;
  isError: boolean;
}

const capitalize = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

/** One label / value / sub block. Shared shape for the hero pair and the secondary triptych. */
function KpiMetric({
  label,
  value,
  sub,
  className,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  sub?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={clsx('min-w-0', className)}>
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.6px] text-lr-muted">
        {label}
      </div>
      <div className="mt-2.5 flex items-baseline whitespace-nowrap">{value}</div>
      {sub != null && sub !== '' && (
        <div className="mt-[5px] whitespace-nowrap text-[11.5px] text-lr-faint">{sub}</div>
      )}
    </div>
  );
}

/**
 * KPI strip (Phase-15 redesign) — the number-priority core. A `1.05fr 1.5fr`
 * grid: a bordered hero card with the desk's two lead figures (Net P&L/day,
 * Rate) and a hairline triptych of three secondary stats (Exposure, Mark vs
 * booking, Margin adequacy).
 *
 * Sources (each must be the ONE source the rest of the page reads, so the
 * cards can't drift — the v1.1 "one helper so figures can't drift" lesson):
 *  - Net P&L / day → `trade.daily_earnings` directly (PnlTable and
 *    AccrualChart projection point at the SAME field via LR-399). Honest-
 *    empty via `isNullOrZeroDecimal`.
 *  - Accrued sub → `accruedByCurrency(trade, accruals)` (sum of
 *    AccrualEntry.net_amount per currency the rows are in, LR-749; real money
 *    already in the books). `deriveAccruedToDate(accruals).days`
 *    (distinct accrual dates) drives the "· N days" counter, matching the
 *    AccrualChart bar count by construction (LR-414 B).
 *  - Margin adequacy → `deriveMarginAdequacy(trade)` (shared with Analytics).
 */
export function KpiStrip({ trade, accruals, isLoading, isError }: KpiStripProps) {
  const currency = trade.currency;
  // LR-414 B: ONE day-basis for the card — `days` is the distinct-accrual-date
  // count, exactly what AccrualChart groups its bars by, so "Accrued · N days" and
  // the chart bar count can't disagree. (Replaces the old calendar settlement→today
  // "days live", which conflated lifecycle days with accrued days — the "32 days
  // live vs 5 chart bars vs 3-days-of-accrual" mismatch from the audit.)
  const { days: accrualDays } = deriveAccruedToDate(accruals ?? []);
  // LR-749: a cross-currency GMSLA trade's rows are booked in the settlement
  // currency, so the sum carries theirs, not the trade's - and a history that
  // straddles the switch shows one sum per currency rather than adding them.
  const accrued = accruedByCurrency(trade, accruals);
  const accruedCurrency = accrualCurrency(trade, accruals);
  const margin = deriveMarginAdequacy(trade);
  // Matured is included alongside closed/cancelled: per spec §5/§6 a matured
  // position is closed (reverse legs posted; accrual stops). Backend currently
  // keeps accruing past maturity (audit N1/N9), so the hero would lie a
  // "-$4.29/day" live earning on a trade that should be frozen — gate to "—"
  // until the backend closure pass lands. Single source of truth: see
  // analytics-economics.TERMINAL_STATUSES (LR-208 r5 M3).
  const isTerminal = isTerminalStatus(trade.status);

  // ── Hero ①: Net P&L / day. Reads `daily_earnings` (signed rate × CV ×
  // direction — the same number PnlTable and the chart projection use), so the
  // three never disagree. Sub keeps the AccrualEntry-sum Accrued total (real
  // money already in books) + days-live counter. Pre-LR-399 this hero summed
  // AccrualEntry.net_amount for the latest accrual_date, which in GMSLA rebate
  // mode was the spread leg — that diverged from the page's other Net figures
  // (TRD-61 KPI $42.18 vs PnlTable $4.39 on the same page, prior audit F2/N2).
  //
  // A null OR ZERO daily falls to "—" rather than a "$0.00" hero (HON-04
  // fresh-trade honest-empty rule).
  //
  // NB: `isError` is NOT in the hero gate any more — the hero reads from
  // `trade.daily_earnings` (not from accruals), so an accruals fetch
  // failure no longer makes the hero figure unreliable. The accruals failure
  // is surfaced under the sub instead ("accruals unavailable") to preserve
  // parity with PnlTable + StickySummary, which also read `trade.daily_*`
  // directly and so never depended on the accruals error in the first place.
  // LR-628: a floating rebate accrues on today's fixing, so every "per day"
  // figure on this page must be priced on the effective rate — the three cards
  // (KpiStrip / StickySummary / PnlTable) stay in lock-step by all reading it.
  const { dailyEarnings, dailyEarningsConverted, totalRate, benchmarkRate, annualEarnings } =
    effectiveRates(trade);
  const hasDaily = !isNullOrZeroDecimal(dailyEarnings);
  // LR-459 → LR-474: for cross-currency trades the SETTLEMENT figure is now the
  // hero and the security-ccy original becomes the `≈` twin (inverted from
  // LR-459 — the trader chose the settlement ccy, so the economics lead in it).
  // Shared predicate (rejects a malformed converted string so it can't render
  // a "≈ —") — KpiStrip / StickySummary / PnlTable MUST stay in lock-step;
  // change all three together.
  const hasSettlementDaily = hasSettlementDailyReturn(trade);
  const heroDashClass = clsx('lr-num', FIGURE_TIER_CLASS.hero, 'text-lr-muted');
  const netNode = isLoading ? (
    <span className="inline-block h-[34px] w-32 animate-pulse rounded bg-gray-100" aria-hidden="true" />
  ) : isTerminal || !hasDaily ? (
    <span className={heroDashClass}>—</span>
  ) : hasSettlementDaily ? (
    <>
      <SignedValue
        value={dailyEarningsConverted}
        currency={trade.settlement_currency || undefined}
        tier="hero"
        demoteDecimals={false}
      />
      <span className="lr-num ml-2 text-[13px] font-semibold text-lr-faint">
        ≈ {formatDecimalString(dailyEarnings, { currency })}
      </span>
    </>
  ) : (
    <SignedValue value={dailyEarnings} currency={currency} tier="hero" demoteDecimals={false} />
  );
  // Hero vs sub carry DIFFERENT bases by design: the hero "Net P&L / day" reads
  // `daily_earnings` (rate × CV × direction — same source as PnlTable +
  // AccrualChart projection, three-card parity); the sub "Accrued" sums real
  // `AccrualEntry.net_amount` rows the back office has booked.
  // LR-414 B: post-LR-395 the booked AccrualEntry row IS the full rebate cash flow
  // (no longer the spread leg only), so for CASH collateral `accrued ≈ daily ×
  // accrual_days` and no caption is warranted. The one base that genuinely still
  // diverges is NON-CASH collateral: the per-day projection (Net P&L / day =
  // daily_earnings) is computed on the collateral value while the booked accrual
  // stays on the contract value (rate.py) — so daily × days and accrued use different
  // bases and need not multiply out. (Even for cash, billing reprices each day on
  // that day's marked CV per LR-399, so the two are only approximately equal — but
  // close enough that an explicit divergence caption would overstate it.)
  const accruedHintTitle =
    'Accrued sums the booked accrual rows verbatim. For non-cash collateral the ' +
    'per-day projection (Net P&L / day) is computed on the collateral value while ' +
    'the booked accrual stays on the contract value, so daily × days and accrued ' +
    'use different bases and need not match exactly.' +
    // LR-474: the hero daily is shown in the settlement currency on a cross-ccy
    // trade; Accrued is too since LR-749, except when every row is still in the
    // security currency (repo, pre-LR-749) — there daily × days also breaks on
    // currency. A split Accrued names both currencies itself.
    (hasSettlementDaily && accruedCurrency === trade.currency
      ? ' Note: the daily figure above is in the settlement currency; Accrued is in the security currency.'
      : '');
  // Mirror the backend gate exactly: daily_earnings diverges from net_amount
  // iff non-cash AND not fee mode — rate.py treats any non-'fee' rate_entry_mode
  // (incl. a null/absent one) as rebate. Gate on `!== 'fee'` (not `=== 'rebate'`)
  // so a non-cash trade with an absent mode still surfaces the real divergence.
  const showAccruedHint =
    trade.collateral_type === 'non_cash' && trade.rate_entry_mode !== 'fee';
  const netSub = isLoading
    ? undefined
    : isError
      ? 'accruals unavailable'
      // Gate on accrualDays (distinct dates), NOT the raw row count, so the empty
      // state matches AccrualChart (which counts the same dated bars): a row missing
      // its accrual_date can't read "Accrued · 0 days" here while the chart reads empty.
      : accrualDays === 0
        ? 'no accruals yet'
        : (
            <>
              Accrued{' '}
              <b className="lr-num font-bold text-lr-fg-2">
                {/* "/" not "+": the two are never added, and a "+" before a negative reads "+ -". */}
                {accrued.map((a) => formatDecimalString(a.net, { currency: a.currency })).join(' / ')}
              </b>
              {showAccruedHint && (
                <span
                  role="img"
                  aria-label="daily and accrued use different bases — hover for explanation"
                  title={accruedHintTitle}
                  className="ml-0.5 inline-flex align-text-bottom text-lr-faint hover:text-lr-muted"
                >
                  <InformationCircleIcon className="h-3 w-3" aria-hidden="true" />
                </span>
              )}{' '}
              · {accrualDays} {accrualDays === 1 ? 'day' : 'days'}{isTerminal ? ' · closed' : ''}
            </>
          );

  // ── Hero ②: Rate (resolved rate as the headline; build-up + annual in the sub).
  // Mode-driven headline so a fee trade reads as a fee and not as a netted total
  // rate. Pre-fix the strip rendered "RATE -1.30%" on a FEE-mode trade where the
  // 4% fee netted against a 5.30% rebate to a meaningless −1.30% (feedback §2b,
  // prior audit F8). Now FEE shows "Fee 4.00%" with the benchmark-rebate leg in
  // the sub; REBATE keeps the resolved total_rate with its build-up.
  const benchmarkName = (trade.benchmark_name || '').toUpperCase();
  // LR-208 r5 L3: null spread is NOT zero — surface the absence in the rate
  // build-up rather than rendering a misleading "SOFR 5.30% − 0 bps". The
  // local `spreadBps` keeps numeric semantics for the formatter via a coerced
  // fallback; `hasSpread` gates whether to show the bps term at all.
  const hasSpread = trade.spread_bps != null;
  const spreadBps = trade.spread_bps ?? 0;
  const rateType = trade.rate_type || 'fixed';
  const isFloatingRebate = rateType === 'floating' && trade.rate_entry_mode === 'rebate';
  const isFeeMode = trade.rate_entry_mode === 'fee';
  // GMRA trades use repo terminology, GMSLA uses rebate. The rate-kind word is
  // driven by the trade type, not the mode-only isFloatingRebate flag (F8a) — a
  // GMRA repo in rebate-entry mode must not read as "rebate".
  const rateKind = GMRA_TRADE_TYPES.includes(trade.trade_type) ? 'repo' : 'rebate';
  const headlineRateValue = isFeeMode ? trade.fee_percent : totalRate;
  const hasRate = toDecimalString(headlineRateValue) != null;
  const rateLabel = isFeeMode ? 'Fee' : isFloatingRebate ? `Rate · ${rateKind}` : 'Rate';
  const rateNode = hasRate ? (
    <span className={clsx('lr-num', FIGURE_TIER_CLASS.hero, 'text-lr-accent')}>
      {formatDecimalString(headlineRateValue, { dp: 2 })}%
    </span>
  ) : (
    <span className={heroDashClass}>—</span>
  );
  // LR-464: total = benchmark + spread. The operator reflects the spread's sign —
  // a positive spread adds ("+ n bps"), a special-stock NEGATIVE spread subtracts
  // ("− n bps") — never a double glyph like "SOFR + -50 bps".
  const spreadTerm = spreadBps >= 0 ? `+ ${spreadBps} bps` : `− ${-spreadBps} bps`;
  // LR-208 r6 M6: gate on a real NON-ZERO value (isNullOrZeroDecimal), not mere
  // presence. The backend returns annual_earnings "0.00" whenever daily is
  // null/zero (e.g. a not-yet-priced floating-rebate/fee trade), and the hero
  // Net P&L/day correctly masks to "—" in that case — so a "· $0/yr" chip next
  // to it was a fabricated zero (HON-04 honest-empty violation).
  const hasAnnual = !isNullOrZeroDecimal(annualEarnings);
  const hasBenchmark = toDecimalString(benchmarkRate) != null;
  // Drop the "$N/yr" chip on terminal trades — the hero is already masked to
  // "—" + "closed"; a live `daily × dc` annual on a frozen position would lie
  // a forward-looking earning (audit N10).
  const annualChip = !isTerminal && hasAnnual && (
    <>
      {' '}
      ·{' '}
      <b className="lr-num font-bold text-lr-fg-2">
        {formatDecimalString(annualEarnings, { currency, dp: 0 })}/yr
      </b>
    </>
  );
  const rateSub = !hasRate
    ? 'rate not set'
    : isFeeMode
      ? (
          <>
            {hasBenchmark
              ? <>{benchmarkName || 'benchmark'} {formatDecimalString(benchmarkRate, { dp: 2 })}% rebate</>
              : 'fee mode'}
            {annualChip}
          </>
        )
      : isFloatingRebate
        ? (
            <>
              {benchmarkName} {formatDecimalString(benchmarkRate, { dp: 2 })}%
              {hasSpread && <> {spreadTerm}</>}
              {annualChip}
            </>
          )
        : (trade.rate_type_display ?? capitalize(rateType));

  // ── Secondary ①: Current exposure (mark × quantity).
  const secondaryDashClass = clsx('lr-num', FIGURE_TIER_CLASS.secondary, 'text-lr-muted');
  const exposureNode =
    margin.exposure != null ? (
      <Figure value={margin.exposure} currency={currency} tier="secondary" />
    ) : (
      <span className={secondaryDashClass}>—</span>
    );
  const exposureSub =
    margin.exposure != null ? (
      <>
        {trade.quantity.toLocaleString('en-US')} sh × mark{' '}
        <span className="lr-num">{formatDecimalString(trade.latest_mark_price, { currency })}</span>
      </>
    ) : (
      'no mark yet'
    );

  // ── Secondary ②: Mark vs booking (signed, per share).
  const hasMarkDelta = margin.markDelta !== '';
  const markNode = hasMarkDelta ? (
    <SignedValue value={margin.markDelta} currency={currency} unit="/sh" tier="secondary" />
  ) : (
    <span className={secondaryDashClass}>—</span>
  );
  const markSub = hasMarkDelta ? (
    <>
      <span className="lr-num">{formatDecimalString(trade.latest_mark_price, { currency })}</span> vs{' '}
      <span className="lr-num">{formatDecimalString(trade.gross_price, { currency })}</span>
    </>
  ) : (
    'no mark vs booking'
  );

  // ── Secondary ③: Margin adequacy (verdict; cannot_assess is a warn chip + reason).
  const adequacyNode =
    margin.verdict === 'cannot_assess' ? (
      <span className="inline-flex items-center gap-1.5 text-[15px] font-bold leading-tight text-lr-warn">
        <ExclamationTriangleIcon className="h-3.5 w-3.5 flex-none" />
        Cannot assess
      </span>
    ) : (
      <span
        className={clsx(
          'text-[15px] font-bold leading-tight',
          verdictTextClass(margin.verdict, margin.exposureExceedsCollateral, margin.isLendSide),
        )}
      >
        {MARGIN_LABEL[margin.verdict]}
      </span>
    );
  const adequacySub =
    margin.verdict === 'no_mark' ? (
      'awaiting first mark'
    ) : margin.verdict === 'cannot_assess' ? (
      'collateral value pending'
    ) : margin.verdict === 'excess' ? (
      <span className="lr-num">+{formatDecimalString(margin.deltaAbs, { currency })}</span>
    ) : (
      <span className="lr-num">−{formatDecimalString(margin.deltaAbs, { currency })}</span>
    );

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.05fr_1.5fr]">
      {/* Hero pair: Net P&L / day · Rate */}
      <div className="flex divide-x divide-lr-border overflow-hidden rounded-[10px] border border-lr-border bg-lr-surface">
        <KpiMetric className="flex-1 px-[22px] py-4" label="Net P&L / day" value={netNode} sub={netSub} />
        <KpiMetric className="flex-1 px-[22px] py-4" label={rateLabel} value={rateNode} sub={rateSub} />
      </div>

      {/* Secondary triptych: Exposure · Mark vs booking · Margin adequacy */}
      <div className="grid grid-cols-3 gap-px overflow-hidden rounded-[10px] border border-lr-border bg-lr-border">
        <KpiMetric
          className="bg-lr-surface px-[17px] py-[14px]"
          label="Current exposure"
          value={exposureNode}
          sub={exposureSub}
        />
        <KpiMetric
          className="bg-lr-surface px-[17px] py-[14px]"
          label="Mark vs booking"
          value={markNode}
          sub={markSub}
        />
        <KpiMetric
          className="bg-lr-surface px-[17px] py-[14px]"
          label="Margin adequacy"
          value={adequacyNode}
          sub={adequacySub}
        />
      </div>
    </div>
  );
}
